import TrillDescription from 'trill-description'

const { TrillHtmlIframeEmbed } = TrillDescription
const { AllowList } = TrillHtmlIframeEmbed

const DEFAULT_WIDTH = 0
const DEFAULT_HEIGHT = 0
const DEFAULT_SANDBOX = 'allow-scripts'

const CLASS_NAME = 'trill-html-iframe-embed'
const SCRIPT_TYPE = 'text/trill-html-iframe-embed'

const allowList = new AllowList()

/**
 * Resolve preview iframe dimensions. Mirrors `_enforceSizeLimits` in
 * `trill-description/index.js` (parseInt-normalized, clamped to the
 * allow-list maxima) so the dialog preview, the TinyMCE bogus preview, and
 * the public-site iframe all stay within the same numeric bounds.
 *
 *   - `mode: 'aspect'` when both width > 0 AND height > 0 — callers can
 *     use `aspect-ratio: W/H` for proportional fluid scaling.
 *   - `mode: 'fluid'` otherwise — callers fall back to their own
 *     width/height (postMessage-driven in the editor, fixed px in the
 *     dialog).
 *
 * Width and height are returned regardless of mode (0 when blank/invalid)
 * so callers can render explicit-px iframes when the editor supplied a value.
 *
 * @param {{ width?, height?, maxWidth?, maxHeight? }} input
 * @returns {{ mode: 'aspect' | 'fluid', width: number, height: number }}
 */
function clampDimension(value, max) {
  const n = parseInt(value, 10) || 0
  if (n <= 0) return 0
  if (typeof max === 'number' && max > 0) return Math.min(n, max)
  return n
}

export function resolveEmbedDimensions(input) {
  const opts = input || {}
  const width = clampDimension(opts.width, opts.maxWidth)
  const height = clampDimension(opts.height, opts.maxHeight)
  return {
    mode: width > 0 && height > 0 ? 'aspect' : 'fluid',
    width,
    height,
  }
}

/**
 * Filters a sandbox token string through the production AllowList so unsafe
 * tokens (notably `allow-same-origin` — which would let the iframe break out
 * of the sandbox and access the parent admin's origin) can never reach the
 * iframe or the persisted marker. Falls back to the AllowList's defaultSandbox
 * when nothing safe survives.
 */
export function safeSandbox(value) {
  return allowList.sanitizeSandbox(value).sandbox
}

/**
 * Builds the `<figure class="trill-html-iframe-embed">` marker that
 * TrillDescription detects on the server. Use this whenever you need to
 * insert the embed into TinyMCE content.
 */
export function buildEmbedHtml({ html, width, height, sandbox }) {
  const safeInner = String(html || '').replace(/<\/script>/gi, '<\\/script>')
  const sanitizedSandbox = safeSandbox(sandbox)
  const attrs = [`class="${CLASS_NAME}"`]
  if (Number.isFinite(width) && width > 0) attrs.push(`data-width="${width}"`)
  if (Number.isFinite(height) && height > 0) attrs.push(`data-height="${height}"`)
  attrs.push(`data-sandbox="${sanitizedSandbox}"`)
  return `<figure ${attrs.join(' ')}><script type="${SCRIPT_TYPE}">${safeInner}</script></figure>`
}

/**
 * Inverse of `buildEmbedHtml` — extracts the original fields from a `<figure>`
 * DOM Element. Pass the Element, not an HTML string.
 *
 * @returns {{html: string, width: number, height: number, sandbox: string} | null}
 */
export function parseEmbedFigure(figureEl) {
  if (!figureEl || figureEl.nodeName !== 'FIGURE') return null
  if (!figureEl.classList || !figureEl.classList.contains(CLASS_NAME)) return null

  const script = figureEl.querySelector(`script[type$="${SCRIPT_TYPE}"]`)
  if (!script) return null
  return {
    html: (script.textContent || '').replace(/<\\\/script>/gi, '</script>'),
    width: Math.max(parseInt(figureEl.getAttribute('data-width'), 10) || DEFAULT_WIDTH, 0),
    height: Math.max(parseInt(figureEl.getAttribute('data-height'), 10) || DEFAULT_HEIGHT, 0),
    sandbox: safeSandbox(figureEl.getAttribute('data-sandbox') || DEFAULT_SANDBOX),
  }
}

/**
 * Matches an embed marker's inner `<script type="text/trill-html-iframe-embed">…</script>`
 * block. `buildEmbedHtml` escapes any literal `</script>` in the pasted HTML, so the
 * non-greedy body never terminates early on user content.
 */
const EMBED_SCRIPT_PATTERN = new RegExp(`<script[^>]*type="[^"]*${SCRIPT_TYPE}"[^>]*>[\\s\\S]*?<\\/script>`, 'gi')

/**
 * Removes embed markers' inner `<script>` source from an HTML string.
 *
 * The pasted HTML lives verbatim inside the embed `<script>` and is rendered only
 * inside a sandboxed iframe, so callers that scan the article body must exclude it —
 * e.g. the external-image-URL validation, which would otherwise flag `<img>` tags
 * that legitimately appear inside the embed source.
 *
 * @param {string} content - article body HTML
 * @returns {string} the body with embed script contents stripped (non-strings returned as-is)
 */
export function stripEmbedScripts(content) {
  if (typeof content !== 'string') return content
  return content.replace(EMBED_SCRIPT_PATTERN, '')
}

export const DEFAULTS = {
  width: DEFAULT_WIDTH,
  height: DEFAULT_HEIGHT,
  sandbox: DEFAULT_SANDBOX,
  className: CLASS_NAME,
  scriptType: SCRIPT_TYPE,
}
